import React, { useEffect, useState } from 'react';
import {
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    FormHelperText,
    Checkbox,
} from '@material-ui/core';
import { useFormikContext, useField } from 'formik';
import type { FormField } from '../interfaces';
import {
    findCamundaPropertyValue,
    parseJsonArrayOfStrings,
    parseTranslatedJsonArrayOfStrings,
} from '../utils';
import { getFormFieldStyles } from '../formFieldStyles';

export const FormikCheckboxGroup = ({
    id: fieldId,
    label: fieldLabel,
    isRequired,
    isReadOnly,
    callback,
    field,
    locale: localeProp,
}: {
    id?: string;
    label?: string;
    isRequired?: boolean;
    isReadOnly?: boolean;
    field?: FormField;
    locale: string;
    callback?: (...params) => void;
}) => {
    const { setFieldValue } = useFormikContext();
    const [formikField, formikFieldMeta] = useField<string | null>(
        fieldId as string,
    );
    const [customCheckboxOptions, setCustomCheckboxOptions] = useState<Array<{
        id: string;
        label: string;
    }> | null>(null);
    const classes = getFormFieldStyles();

    const locale = localeProp;

    const customCheckboxOptionField = findCamundaPropertyValue(
        'items',
        field?.properties,
    );

    const selectedValues = parseJsonArrayOfStrings(formikField.value);

    const isChecked = (id: string, label: string) => {
        return (
            selectedValues.includes(id?.toLocaleLowerCase()) ||
            selectedValues.includes(label?.toLocaleLowerCase())
        );
    };

    const handleChange = (event) => {
        const { value, checked } = event.target;

        if (customCheckboxOptionField) {
            const updatedValues = checked
                ? [...selectedValues, value]
                : selectedValues?.filter((v) => v !== value);

            setFieldValue(
                fieldId as string,
                updatedValues?.length > 0
                    ? JSON.stringify(updatedValues)
                    : null,
            );

            if (callback) {
                callback(
                    updatedValues?.length > 0
                        ? JSON.stringify(updatedValues)
                        : null,
                );
            }
        } else {
            setFieldValue(fieldId as string, checked);

            if (callback) {
                callback(checked);
            }
        }
    };

    useEffect(() => {
        if (!customCheckboxOptionField) return;
        setCustomCheckboxOptions(
            parseTranslatedJsonArrayOfStrings(
                customCheckboxOptionField,
                locale,
            ),
        );
    }, [customCheckboxOptionField, locale]);

    return (
        <FormControl
            className={classes.editableField}
            error={Boolean(formikFieldMeta?.error) && formikFieldMeta?.touched}
            component="fieldset"
            disabled={isReadOnly}
            required={isRequired}
        >
            {customCheckboxOptions ? (
                <FormLabel component="legend">{fieldLabel}</FormLabel>
            ) : null}
            <FormGroup>
                {customCheckboxOptions ? (
                    customCheckboxOptions?.map(({ id, label }) => {
                        return (
                            <FormControlLabel
                                key={String(id?.toLowerCase())}
                                value={String(id?.toLowerCase())}
                                control={
                                    <Checkbox
                                        name={id}
                                        value={String(id?.toLowerCase())}
                                        checked={isChecked(id, label)}
                                        onChange={handleChange}
                                        disabled={isReadOnly}
                                    />
                                }
                                label={label}
                                disabled={isReadOnly}
                            />
                        );
                    })
                ) : (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    name={fieldId}
                                    checked={Boolean(formikField?.value)}
                                    onChange={handleChange}
                                    disabled={isReadOnly}
                                />
                            }
                            label={fieldLabel}
                            disabled={isReadOnly}
                        />
                    </>
                )}
                {formikFieldMeta?.touched && formikFieldMeta?.error && (
                    <FormHelperText
                        id="component-error-text"
                        className={classes.error}
                    >
                        {formikFieldMeta?.error}
                    </FormHelperText>
                )}
            </FormGroup>
        </FormControl>
    );
};
